import { useEffect, type MutableRefObject } from "react";
import type { Terminal } from "@xterm/xterm";
import type { FitAddon } from "@xterm/addon-fit";

/**
 * Non-destructive repaint + refit on window focus / visibility change.
 *
 * Replaces the old pattern of clear()+replay which garbled live terminals
 * by interleaving replayed and incoming PTY data.
 */
export function useTerminalFocusRefit(
  terminalsRef: MutableRefObject<Map<string, Terminal>>,
  fitAddonsRef: MutableRefObject<Map<string, FitAddon>>,
  activeTabId: string | null | undefined,
  layoutMode: "tabs" | "grid",
  fitAndSyncTab: (tabId: string) => void
) {
  useEffect(() => {
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;

    const refresh = () => {
      if (document.hidden) return;
      // Leading-edge throttle: focus + visibilitychange often fire together.
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => { throttleTimer = null; }, 200);

      requestAnimationFrame(() => {
        const refit = (tabId: string) => {
          const terminal = terminalsRef.current.get(tabId);
          if (terminal) terminal.refresh(0, terminal.rows - 1);
          fitAndSyncTab(tabId);
        };

        if (layoutMode === "tabs") {
          if (activeTabId) refit(activeTabId);
          return;
        }
        fitAddonsRef.current.forEach((_, tabId) => refit(tabId));
      });
    };

    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [terminalsRef, fitAddonsRef, activeTabId, layoutMode, fitAndSyncTab]);
}
